// RulesCardPanel.tsx — (tf3-38; Creative 2 C2.0-2 §3) the rules card at the storyboard, in the Run Inspector: the card as the
// gate shows it (contracts rulesCardGateRules, read from the workers), and a person's edit of the fields the workers offer.
// Shown while the run is parked at its storyboard gate or held by the storyboard check (the only places the workers take
// an edit). After a save the card is read again and `onEdited` tells the page, which keeps Approve hidden until the gate
// parks again; after a refusal the card is read again too (it may have changed: CONFLICT), and the refusal is shown.
import { useEffect, useState } from "react";
import { opsApi } from "@/api/ops.ts";
import { CARD_FIELDS, editOf, formOf, savedLine, type RulesCardRead } from "@/lib/rules-card.ts";

export function RulesCardPanel({ runId, where, onEdited }: { runId: string; where: "gate" | "held"; onEdited: (at: number) => void }) {
  const [read, setRead] = useState<RulesCardRead | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string> | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [seq, setSeq] = useState(0);   // a re-read
  useEffect(() => {
    let alive = true;   // a late answer for an older read is dropped
    setReadErr(null);
    opsApi.rulesCard(runId).then((r) => { if (alive) setRead(r); })
      .catch((e) => { if (alive) { setRead(null); setReadErr(String((e as Error)?.message ?? e)); } });   // catch-reason: shown in place of the card
    return () => { alive = false; };
  }, [runId, seq]);

  if (readErr) return <div style={{ fontSize: 13, margin: "8px 0", color: "#92400e" }}><strong>The rules card</strong> couldn't be read: {readErr}</div>;
  if (!read) return <div className="tf-muted" style={{ fontSize: 13 }}>Reading the rules card…</div>;
  const fields = CARD_FIELDS.filter((f) => read.editable.includes(f.id));
  const save = async () => {
    if (!form) return;
    const e = editOf(read.card, form, read.editable);
    if ("error" in e) { setMsg({ ok: false, text: e.error }); return; }
    setBusy(true); setMsg(null);
    try {
      const s = await opsApi.editRulesCard(runId, e.edit, reason);
      onEdited(Date.now());
      setMsg({ ok: true, text: savedLine(s) }); setForm(null); setReason("");
    } catch (x) { setMsg({ ok: false, text: String((x as Error)?.message ?? x) }); }   // catch-reason: the refusal is shown, and the card read again
    finally { setBusy(false); setSeq((n) => n + 1); }
  };
  return (
    <div style={{ fontSize: 13, margin: "8px 0", padding: 10, border: "1px solid #e5e7eb", borderRadius: 6 }}>
      <div><strong>The rules card</strong> <span className="tf-muted">(version {read.gateRules.version}{where === "held" ? "; the run is held by the storyboard check" : ""})</span></div>
      <ul style={{ margin: "4px 0 0 18px", fontSize: 12 }}>{read.gateRules.rules.map((r) => <li key={r.id}>{r.text}</li>)}</ul>
      {msg && <div style={{ marginTop: 6, color: msg.ok ? "#166534" : "#b91c1c" }}>{msg.text}</div>}
      {!form ? <button disabled={busy || !fields.length} onClick={() => { setForm(formOf(read.card)); setMsg(null); }} style={{ marginTop: 6 }}>Edit the card</button> : (
        <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "minmax(160px, max-content) 1fr", gap: "4px 8px", alignItems: "center", maxWidth: 560 }}>
          {fields.map((f) => <label key={f.id} style={{ display: "contents" }}><span>{f.label}</span>
            {typeof f.kind === "object" || f.kind === "bool"
              ? <select value={form[f.id] ?? ""} onChange={(ev) => setForm({ ...form, [f.id]: ev.target.value })}>
                  {(f.kind === "bool" ? ["true", "false"] : [...(f.kind.empty ? [""] : []), ...f.kind.one]).map((o) =>
                    <option key={o} value={o}>{o === "" ? "(none)" : o === "true" ? "yes" : o === "false" ? "no" : o}</option>)}
                </select>
              : <input value={form[f.id] ?? ""} onChange={(ev) => setForm({ ...form, [f.id]: ev.target.value })} />}</label>)}
          <span>Why (optional)</span><input value={reason} maxLength={500} onChange={(ev) => setReason(ev.target.value)} />
          <span />
          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save the card"}</button>
            <button disabled={busy} onClick={() => { setForm(null); setMsg(null); }}>Cancel</button>
          </div>
          <span />
          <div className="tf-muted" style={{ fontSize: 12 }}>A new pacing re-makes the storyboard from continuity (image calls, no video); anything else
            re-runs the storyboard check. The gate goes back to waiting until it parks again.</div>
        </div>)}
    </div>
  );
}
